import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, tokens } from '@fluentui/react-components';
import { hasCodeConvention } from '@moko/utils/src/authHelper';
import { getTenantIdFromToken } from '@moko/utils/src/tenantHelper';
import { Features, useFeatureFlag } from '@moko/utils/src/featureAccess';
import {
  siteAvailabilityDraft,
  type IEntitiesMetadatasResponse,
  type IField,
  type IMetadata,
  type OrderSiteAvailabilityWindow,
} from '@moko/utils';
import EditItem from '../EditItem';
import MokoCard from '../Layout/MokoCard';
import { OrderSiteAvailability } from '../OrderSiteAvailability';
import useFetch from '../hooks/useFetch';
import { pricingItemLookups } from '../OtherInfo/fields/pricingItemLookups';
import { useOrderComponentSupport } from '../OtherInfo/useOrderComponentSupport';
import type { WorkComponentProps, IWorkOrderEditServices } from './types';
import {
  type GetOrderService,
  type GetOrderMetadataService,
} from './OrderDetailsDrawer';
import {
  buildGroupedOrderDetailFields,
  filterOrderedMetadatas,
  flattenOrderFormValues,
  splitOrderFormValues,
} from './orderDetailFields';
import { useTenantWorkTypes } from './useTenantWorkTypes';
import { workTypeReadLabel } from './WorkTypeField';
import ContentSkeleton from '../ContentSkeleton/ContentSkeleton';

export interface WorkOrderTabProps extends WorkComponentProps {
  orderId: number;
  getOrder: GetOrderService;
  getMetadata?: GetOrderMetadataService;
  services: IWorkOrderEditServices;
  getTenantWorkTypes?: (
    abortController: AbortController,
    tenantId?: number
  ) => Promise<{ id?: number; name: string }[]>;
  workTypeId?: number | null;
  canToggleEdit: boolean;
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
}

const relatedAssetIdsOf = (order: Record<string, unknown>): number[] => {
  const related = order.relatedAssets;
  if (!Array.isArray(related)) return [];
  return related
    .map((asset) => Number((asset as { id?: unknown }).id))
    .filter((id) => Number.isFinite(id) && id > 0);
};

const siteAvailabilityOf = (order: Record<string, unknown>) => {
  const raw = order.siteAvailabilityWindows;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((row) => {
    try {
      return [siteAvailabilityDraft(row as OrderSiteAvailabilityWindow)];
    } catch {
      return [];
    }
  });
};

const metadataKeysOf = (
  metadatas: IEntitiesMetadatasResponse['metadatas'] | undefined
): { key: string; value: string }[] => {
  if (!metadatas) return [];
  const keys =
    metadatas instanceof Map
      ? [...metadatas.keys()]
      : Object.keys(metadatas as unknown as Record<string, unknown>);
  return keys.map((key) => ({
    key: String(key),
    value: String(key).split('|')[1] ?? String(key),
  }));
};

const useStyles = makeStyles({
  actions: {
    boxSizing: 'border-box',
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXXL}`,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  availability: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
});

const WorkOrderTab = ({
  orderId,
  getOrder,
  getMetadata,
  services,
  getTenantWorkTypes,
  workTypeId: workTypeIdFallback,
  canToggleEdit,
  editing,
  onEditingChange,
  setIsInProgress,
  setSuccessMessage,
  setErrorMessage,
}: WorkOrderTabProps) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const isSaved = useRef(false);
  const orderSnapshot = useRef<Record<string, unknown>>({});
  const [orderedEntityType, setOrderedEntityType] = useState<string>();

  const { isTenantReady, isEnabled: isOrderAssetEnabled } = useFeatureFlag(
    Features.FEATURE_ORDER_ASSET
  );
  const forceWorkEntity = isTenantReady && !isOrderAssetEnabled;
  const hideCode = useMemo(() => hasCodeConvention('order'), []);

  const orderFetch = useFetch<Record<string, unknown>>({
    queryKey: ['work-order-tab', orderId],
    queryFn: (abortController) => getOrder(abortController, orderId),
    onError: (error) =>
      setErrorMessage(error.message || t('failedToLoad', 'Failed to load')),
  });

  const order = orderFetch.data;
  const metadataName = order
    ? String(order.metadataName ?? '').includes('|')
      ? String(order.metadataName).split('|')[1]
      : String(order.metadataName ?? '')
    : '';
  const orderTenantId =
    typeof order?.tenantId === 'number'
      ? order.tenantId
      : Number.parseInt(String(order?.tenantId ?? ''), 10);
  const statusesTenantId = Number.isFinite(orderTenantId)
    ? orderTenantId
    : getTenantIdFromToken() ?? undefined;

  const metadataFetch = useFetch<IMetadata>({
    queryKey: ['work-order-tab-metadata', metadataName],
    enabled: !!getMetadata && metadataName !== '',
    queryFn: (abortController) =>
      getMetadata!(abortController, 'Order', undefined, metadataName),
  });

  const statusesFetch = useFetch<{ id: number; name: string }[]>({
    queryKey: ['work-order-tab-statuses', statusesTenantId],
    enabled: statusesTenantId != null,
    queryFn: (abortController) =>
      services.getOrderStatuses(abortController, statusesTenantId),
  });

  const pricingOptionsFetch = useFetch({
    queryKey: ['work-order-pricing-options'],
    queryFn: (abortController) =>
      services.getOrderPricingOptions(abortController),
  });

  const entitiesFetch = useFetch<IEntitiesMetadatasResponse>({
    queryKey: ['work-order-tab-entities'],
    enabled: isTenantReady,
    queryFn: (abortController) =>
      services.getEntitiesMetadatas(abortController, ['Work', 'Asset']),
  });

  const companyMetadata = metadataFetch.data;
  const orderComponents = useOrderComponentSupport({
    fields: companyMetadata?.fields,
    services,
    enabled: !!order,
    selectedTimeServiceId:
      typeof order?.timeServiceId === 'number'
        ? order.timeServiceId
        : undefined,
  });
  const orderStatuses = statusesFetch.data ?? [];
  const entityMetadatas = metadataKeysOf(entitiesFetch.data?.metadatas);
  const effectiveEntityType = forceWorkEntity
    ? 'Work'
    : orderedEntityType || String(order?.orderedEntityType ?? '');
  const orderedEntitiesMetadatas = filterOrderedMetadatas(
    entityMetadatas,
    effectiveEntityType
  );
  const savedWorkTypeId =
    typeof order?.workTypeId === 'number'
      ? order.workTypeId
      : typeof workTypeIdFallback === 'number'
      ? workTypeIdFallback
      : null;
  const workTypes = useTenantWorkTypes(
    Number.isFinite(orderTenantId) ? orderTenantId : undefined,
    effectiveEntityType === 'Work' && typeof getTenantWorkTypes === 'function',
    getTenantWorkTypes
  );

  const metadata = useMemo<IMetadata>(() => {
    const tenantId =
      order?.tenantId != null ? String(order.tenantId) : undefined;
    const tenantName =
      typeof order?.tenantName === 'string' && order.tenantName
        ? order.tenantName
        : tenantId
        ? `Tenant #${tenantId}`
        : '';

    const companyPanel = 'orderCompanyInfo';
    const companyFields = (companyMetadata?.fields ?? []).map((field) => {
      const parent = field.parentName;
      const isRoot = parent == null || parent === '' || parent === 'root';
      if (!isRoot || field.fieldType === 'Panel') return field;
      return { ...field, parentName: companyPanel };
    });
    const fields: IField[] = [
      ...buildGroupedOrderDetailFields(
        t,
        forceWorkEntity,
        (field, base) => {
          if (field.hiddenInEdit) return;
          if (field.hiddenWhenForceWork && forceWorkEntity) return;
          if (
            field.key === 'workType' &&
            (effectiveEntityType !== 'Work' ||
              typeof getTenantWorkTypes !== 'function')
          ) {
            return;
          }
          if (field.key === 'tenant') return { ...base, readonly: true };
          if (field.key === 'code') return { ...base, readonly: hideCode };
          if (field.key === 'specialInstruction') {
            const text =
              typeof order?.specialInstruction === 'string'
                ? order.specialInstruction.trim()
                : '';
            if (!text && !editing) return;
          }
          return base;
        },
        { chrome: false, sectionTitle: true, sectionSplit: true }
      ),
      ...(companyFields.some((field) => field.parentName === companyPanel)
        ? [
            {
              name: companyPanel,
              fieldType: 'Panel' as const,
              label: t('companySpecificInfo', 'Company specific info'),
              fullWidth: true,
              options: {
                columns: 1,
                chrome: false,
                sectionTitle: true,
                sectionSplit: true,
              },
            },
          ]
        : []),
      ...companyFields,
    ];

    return {
      fields,
      lookups: [
        {
          name: 'tenants',
          values: tenantId ? [{ key: tenantId, value: tenantName }] : [],
        },
        {
          name: 'Statuses',
          values: orderStatuses.map((status) => ({
            key: String(status.id),
            value: status.name,
          })),
        },
        {
          name: 'entityTypes',
          values: [
            { key: 'Work', value: 'Work' },
            { key: 'Asset', value: 'Asset' },
          ],
        },
        {
          name: 'workTypes',
          values: [
            { key: 'none', value: t('noWorkType', 'No work type') },
            ...workTypes.options.map((option) => ({
              key: String(option.id),
              value: option.name,
            })),
            ...(savedWorkTypeId != null &&
            !workTypes.loading &&
            !workTypes.options.some((option) => option.id === savedWorkTypeId)
              ? [
                  {
                    key: String(savedWorkTypeId),
                    value: workTypeReadLabel(
                      savedWorkTypeId,
                      workTypes.options,
                      t
                    ),
                  },
                ]
              : []),
          ],
        },
        {
          name: 'orderedEntitiesMetadatas',
          values: orderedEntitiesMetadatas,
        },
        {
          name: 'pricingItems',
          values: pricingItemLookups(pricingOptionsFetch.data ?? []),
        },
        ...(companyMetadata?.lookups ?? []),
      ],
      validations: companyMetadata?.validations ?? [],
      interactions: companyMetadata?.interactions ?? [],
    };
  }, [
    companyMetadata,
    effectiveEntityType,
    forceWorkEntity,
    getTenantWorkTypes,
    hideCode,
    savedWorkTypeId,
    workTypes.loading,
    workTypes.options,
    editing,
    order,
    orderStatuses,
    orderedEntitiesMetadatas,
    pricingOptionsFetch.data,
    t,
  ]);

  if (orderFetch.loading) {
    return <ContentSkeleton variant="form" rows={6} />;
  }
  if (!order) return null;

  return (
    <MokoCard style={{ padding: 0 }}>
      <EditItem
        actionsClassName={styles.actions}
        itemIdOverride={orderId}
        actionsPlacement="bottom"
        metadata={metadata}
        isSaved={isSaved}
        otherInfoReadOnly
        orderComponentSupport={orderComponents.support}
        otherInfoCanToggleEdit={canToggleEdit}
        editing={editing}
        onEditingChange={onEditingChange}
        onFetchUsers={services.getUsers}
        afterChange={(next) => {
          const nextType = next.orderedEntityType;
          if (typeof nextType === 'string') setOrderedEntityType(nextType);
        }}
        getAction={async (abortController) => {
          setIsInProgress(true);
          try {
            const loaded = (await getOrder(abortController, orderId)) as Record<
              string,
              unknown
            >;
            orderSnapshot.current = loaded;
            const flattened = flattenOrderFormValues(loaded);
            if (
              (flattened.workTypeId === '' ||
                flattened.workTypeId === 'none') &&
              typeof workTypeIdFallback === 'number'
            ) {
              flattened.workTypeId = String(workTypeIdFallback);
            }
            return flattened;
          } catch (error: unknown) {
            const message =
              error instanceof Error
                ? error.message
                : t('failedToLoad', 'Failed to load');
            setErrorMessage(message);
            return {};
          } finally {
            setIsInProgress(false);
          }
        }}
        newAction={() => {
          throw new Error('Function not implemented.');
        }}
        editAction={async (abortController, id, edited) => {
          try {
            const loaded = orderSnapshot.current;
            const split = splitOrderFormValues(edited);
            await services.updateOrder(abortController, id, {
              code: split.code,
              description: split.description,
              statusId: split.statusId,
              metadataName:
                typeof loaded.metadataName === 'string'
                  ? loaded.metadataName
                  : undefined,
              orderedEntityType: forceWorkEntity
                ? 'Work'
                : split.orderedEntityType,
              orderedMetadataName: split.orderedMetadataName,
              actualPrice: split.actualPrice,
              otherInfo: orderComponents.prune(split.otherInfo),
              relatedAssetIds: relatedAssetIdsOf(loaded),
              pricingItems: split.pricingItems,
            });
            setSuccessMessage(
              t(
                'orderUpdatedWithId',
                'Order with id: {{id}} successfully updated.',
                { id }
              )
            );
          } catch (error: unknown) {
            const message =
              error instanceof Error
                ? error.message
                : t('failedToUpdate', 'Failed to update');
            setErrorMessage(message);
          }
        }}
      />
      <div className={styles.availability}>
        <OrderSiteAvailability
          layout="split"
          readOnly
          value={siteAvailabilityOf(order)}
        />
      </div>
    </MokoCard>
  );
};

export default WorkOrderTab;
